import { Trans, useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useVerifyOtpMutation,
  useRequestOtpMutation,
} from "@/stores/queries/payments.query";
import { getApiErrorMessage } from "@/utils/api-error.utils";
import { toast } from "@/utils/toast.utils";
import { useTimer } from "@/hooks/useTimer";
import { MezzaOtpField } from "@/components/core/Form/MezzaOtpField";
import { PaymentSuccessResponse } from "./types";
import { formatPhoneNumber } from "@/utils/string.utils";
import { ButtonWithLoading } from "@/components/core/Form/ButtonWithLoading";
import { cn } from "@/lib/utils";
import { createOtpSchema } from "@/common/validation.common";
import { OTP_CONFIG } from "@/consts/otp.consts";

interface OtpVerificationProps {
  otpPaymentId: string;
  memberId: string;
  locationId: string;
  amountToPay: number;
  tipAmount: number;
  memberPhoneNumber: string;
  note?: string;
  onBack: () => void;
  onSuccess: (paymentData: PaymentSuccessResponse) => void;
  onResendOtp: (otpPaymentId: string) => void;
}

export function OtpVerification({
  otpPaymentId,
  memberId,
  locationId,
  amountToPay,
  tipAmount,
  memberPhoneNumber,
  note,
  onBack,
  onSuccess,
  onResendOtp,
}: OtpVerificationProps) {
  const { t } = useTranslation();
  const { isCompleted, resetTimer, formattedTime } = useTimer({
    initialTime: OTP_CONFIG.TIMER_DURATION,
  });

  const { mutate: verifyOtp, isPending: isVerifying } = useVerifyOtpMutation();
  const { mutate: requestOtp, isPending: isResendingOtp } =
    useRequestOtpMutation();

  // OTP validation schema
  const otpSchema = createOtpSchema(t);
  type OtpFormValues = z.infer<typeof otpSchema>;

  const form = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: "",
    },
    mode: "onBlur",
  });

  // Handle OTP verification
  const handleSubmit = (values: OtpFormValues) => {
    verifyOtp(
      {
        otpPaymentId,
        otpCode: values.otp,
        locationId,
      },
      {
        onSuccess: (response) => {
          onSuccess(response);
        },
        onError: (error) => {
          form.setValue("otp", "");
          const message = getApiErrorMessage(error, t("verification.invalidOtp"));
          form.setError("otp", { message });
        },
      }
    );
  };

  // Resend OTP
  const handleResend = () => {
    requestOtp(
      {
        memberId,
        locationId,
        amountToPay,
        tipAmount,
        note,
      },
      {
        onSuccess: (response) => {
          // Reset the timer
          resetTimer();

          if(onResendOtp) {
            onResendOtp(response.otpPaymentId);
          }

          toast.success(t("verification.otpResent"));
        },
        onError: () => {
          toast.error(t("verification.otpResendFailed"));
        },
      }
    );
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold">{t("verification.heading")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("verification.description")}
          </p>
        </div>
      </CardHeader>

      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col space-y-6"
          >
            <MezzaOtpField
              form={form}
              name="otp"
              label="verification.otpLabel"
              length={OTP_CONFIG.LENGTH}
              className="mx-auto"
              autoFocus
              onComplete={(value) => {
                form.setValue("otp", value);
                form.handleSubmit(handleSubmit)();
              }}
            />

            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                <Trans
                  i18nKey="verification.otpSentTo"
                  values={{ phoneNumber: formatPhoneNumber(memberPhoneNumber) }}
                />
              </p>
            </div>

            <div className="text-center">
              {isCompleted ? (
                <ButtonWithLoading
                  type="button"
                  variant="link"
                  onClick={handleResend}
                  disabled={isResendingOtp}
                  className={cn("p-0 h-auto", isResendingOtp && "!bg-transparent")}
                  isLoading={isResendingOtp}
                >
                  {t("verification.resendOtp")}
                </ButtonWithLoading>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t("verification.resendIn")} {formattedTime}
                </p>
              )}
            </div>

            <div className="flex flex-col space-y-3">
              <ButtonWithLoading
                type="submit"
                className="w-full"
                disabled={isVerifying || isResendingOtp}
                isLoading={isVerifying}
              >
                {t("verification.submit")}
              </ButtonWithLoading>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={onBack}
              >
                {t("verification.cancel")}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
